/**
 * @fileoverview Top navigation bar component with language selector
 */
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';

// Fallback flags for languages if they don't have them in the DB
const LANGUAGE_FLAGS = {
  'es': '🇪🇸',
  'en': '🇬🇧',
  'de': '🇩🇪',
  'fr': '🇫🇷',
  'it': '🇮🇹',
  'pt': '🇧🇷',
  'eu': '🏁' // Ikurriña fallback if needed
};

export const Navbar = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const { t, currentLanguage, setLanguage, languages } = useLanguage();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <nav className="h-16 bg-white border-b border-slate-200 px-4 flex items-center justify-between sticky top-0 z-30 lg:static">
      <div className="flex flex-row items-center justify-start gap-4">
        {/* Mobile Menu Button */}
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors focus:outline-none"
          aria-label="Abrir menú"
        >
          <i className="fas fa-bars text-xl"></i>
        </button>

        {/* Mobile Logo */}
        <div className="lg:hidden flex items-center">
          <img src="/logo_transparente.png" alt="TrueDok" className="h-8 w-auto object-contain" />
        </div>
      </div>

      <div className="flex items-center gap-2">
        {/* Language Selector */}
        <div className="relative">
          <button
            className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all text-sm focus:outline-none"
            onClick={() => { setIsLangOpen(!isLangOpen); setIsMenuOpen(false); }}
            onBlur={() => setTimeout(() => setIsLangOpen(false), 200)}
            title={t('nav.change_language', 'Cambiar idioma')}
          >
            <i className="fas fa-globe text-blue-500 text-xs"></i>
            <span className="uppercase font-semibold text-xs">{currentLanguage}</span>
            <i className={`fas fa-chevron-down text-[9px] text-slate-400 transition-transform duration-200 ${isLangOpen ? 'rotate-180' : ''}`}></i>
          </button>

          {isLangOpen && (
            <div className="absolute top-full right-0 mt-2 w-44 bg-white rounded-xl shadow-xl border border-slate-100 py-1.5 z-50 animate-fade-in-up">
              <div className="px-3 py-1.5 border-b border-slate-50">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{t('nav.language', 'Idioma')}</p>
              </div>
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    setLanguage(lang.code);
                    setIsLangOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors text-sm flex items-center justify-between gap-2 ${currentLanguage === lang.code ? 'text-blue-600 font-bold bg-blue-50/50' : 'text-slate-600'
                    }`}
                >
                  <span className="flex items-center gap-2">
                    <span className="text-base">{lang.flag || LANGUAGE_FLAGS[lang.code] || '🏳️'}</span>
                    <span>{lang.native_name || lang.name}</span>
                  </span>
                  {currentLanguage === lang.code && <i className="fas fa-check text-[10px] text-blue-500"></i>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* User Menu */}
        {user && (
          <div className="relative">
            <button
              className="flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50 transition-all focus:outline-none group"
              onClick={() => { setIsMenuOpen(!isMenuOpen); setIsLangOpen(false); }}
              onBlur={() => setTimeout(() => setIsMenuOpen(false), 200)}
            >
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-[#192A42] to-[#233b5c] text-white flex items-center justify-center font-bold text-lg shadow-md shadow-slate-200 group-hover:shadow-blue-200 transition-all overflow-hidden">
                {user.avatarUrl ? (
                  <img src={user.avatarUrl.startsWith('data:') ? user.avatarUrl : user.avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  user.username.charAt(0).toUpperCase()
                )}
              </div>
              <div className="hidden sm:block text-left">
                <span className="block text-sm font-semibold text-slate-700 group-hover:text-blue-600 transition-colors">
                  {user.username}
                </span>
                <span className="block text-xs text-slate-400 font-medium">
                  {user.role === 'certifier' ? t('common.admin', 'Administrador') : t('common.user', 'Usuario')}
                </span>
              </div>
              <i className={`fas fa-chevron-down text-xs text-slate-400 transition-transform duration-200 ${isMenuOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {isMenuOpen && (
              <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-slate-100 py-2 z-50 animate-fade-in-up">
                <div className="px-4 py-3 border-b border-slate-50">
                  <p className="text-sm font-semibold text-slate-800 truncate">{user.email}</p>
                  <p className="text-xs text-slate-400 font-medium uppercase mt-0.5">{user.role}</p>
                </div>
                <div className="p-1">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <i className="fas fa-sign-out-alt"></i>
                    {t('nav.logout', 'Cerrar Sesión')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};
